import { Flex } from 'lib/components/styles/layout';
import React, { useRef } from 'react';
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  PixelCrop,
  type Crop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { SliderControl } from '../slider-control/SliderControl';
import { useDebounceEffect } from './useDebounceEffect';
import { canvasPreview } from './canvasPreview';
import { CropWrapper } from './styles';

interface IImageCropEditorEditorProps {
  disabled?: boolean;
  onPreviewChange: (canvas: HTMLCanvasElement | null) => void;
  src: string;
  crop: Crop | undefined;
  setCrop: React.Dispatch<React.SetStateAction<Crop | undefined>>;
  scale?: number;
  setScale?: React.Dispatch<React.SetStateAction<number>>;
  aspect?: number;
  onCropComplete: React.Dispatch<React.SetStateAction<PixelCrop | undefined>>;
  completedCrop: PixelCrop | undefined;
  imageRef: React.RefObject<HTMLImageElement>;

  // Slider control props
  step?: number;
  max?: number;
  min?: number;
}

const centerAspectCrop = (
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) => {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 100,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
};

export const ImageCropEditor = ({
  disabled = false,
  onPreviewChange,
  src,
  crop,
  setCrop,
  scale,
  setScale,
  aspect,
  onCropComplete,
  completedCrop,
  imageRef,
  max = 5,
  min = 1,
  step = 0.1,
}: IImageCropEditorEditorProps) => {
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    if (aspect) {
      // if an aspect ratio is provided, center the crop
      setCrop(centerAspectCrop(width, height, aspect));
    } else {
      // if no aspect ratio, default to a 100% crop
      setCrop({
        unit: '%',
        x: 0,
        y: 0,
        width: 100,
        height: 100,
      });
    }
  };

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imageRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(
          imageRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale
        );
        onPreviewChange(previewCanvasRef.current);
      } else {
        onPreviewChange(null);
      }
    },
    100,
    [completedCrop, scale]
  );

  const showSliderControl = setScale && scale;

  return (
    <Flex gap='32px'>
      <CropWrapper>
        <ReactCrop
          crop={crop}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
          aspect={aspect}
          minHeight={100}
          onComplete={onCropComplete}
          disabled={disabled}
        >
          <img
            src={src}
            ref={imageRef}
            style={{
              transform: `scale(${scale})`,
              maxWidth: '100%',
              maxHeight: '400px',
            }}
            onLoad={onImageLoad}
            alt='Crop preview'
          />
        </ReactCrop>
      </CropWrapper>
      {!!crop && (
        <canvas
          ref={previewCanvasRef}
          style={{
            display: 'none',
            border: '1px solid black',
            objectFit: 'contain',
            width: crop.width,
            height: crop.height,
          }}
        />
      )}
      {showSliderControl && (
        <SliderControl
          value={scale}
          onChange={setScale}
          max={max}
          min={min}
          step={step}
        />
      )}
    </Flex>
  );
};
